import {
    CommentSettings,
    FeatureType
} from '../../../types';
import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators';

import { ErrorToastElement } from '../../error-toast';
import { StateController } from '../../../helpers/state/state-management/state-controller';
import accountInfo from '../../../helpers/state/account-info';
import featuresInfo from '../../../helpers/state/features-info';
import { isDefined } from '../../../helpers/utils';
import topicData from '../../../helpers/state/topic-data';

@customElement('admin-comment-addon')
export class AdminCommentAddon extends LitElement {
    amplitude_callsite: string = 'Comment Settings Drawer';

    @query('#errorToast')
    private readonly errorToastElement?: ErrorToastElement;

    @state()
    selected_tab: string = 'Comments';

    options: string[] = ['Comments'];

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    openUpgradeDrawer() {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: FeatureType.CustomizeAddPointForm
                }
            })
        );
    }

    handleChangeTab(e: CustomEvent) {
        this.tabIndex = e.detail.tabIndex;
        this.selected_tab = this.options[this.tabIndex];
    }


    render() {
        if (!topicData.isReady) {
            return html`<loading-element loadingString="Getting Comment Data..."></loading-element>`;
        }
        return html`
            <div class="wrapper">
                <div class="header-title-and-buttons" style="background-color: ${topicData.backgroundColor}; color: ${topicData.textColor}">
                    <div class="left-header-content">
                        <form-button
                            size="md"
                            variant="text"
                            icon="arrow_back"
                            @click=${() => this.dispatchEvent(new CustomEvent('close-fragment'))}
                        >Back</form-button>
                    </div>
                    <div class="middle-header-content">
                        Commenting Feature
                    </div>

                    <div class="right-header-content">
                    </div>
                </div>
                <div class="tabs">
                    <tabs-component
                        .options=${this.options}
                        .tabIndex=${this.selected_tab === 'Comments' ? 0 : 1}
                        @on-select-tab=${this.handleChangeTab}
                    ></tabs-component>
                </div>
                <div class="tab__content">
                    ${this.selected_tab === 'Comments' ? this.renderCommentSettings() : ''}
                </div>
            </div>
                        <mwc-dialog
                id="electionModal"
                class="dialog"
                hideActions
                scrimClickAction=""
                escapeKeyAction=""
            >
        `;
    }

    async enableCommentsChange(e: CustomEvent) {
        const comment_settings = topicData.topic?.comment_settings;
        if (comment_settings && topicData.topic?._id) {
            comment_settings.allow_comments = !comment_settings.allow_comments;
            const new_comment_settings = {...comment_settings};
            await topicData.fetchAndUpdateTopicData({comment_settings: new_comment_settings}, this.amplitude_callsite);
        } else {
            // create new comment settings
            const new_comment_settings: CommentSettings  = {
                allow_comments: true,
                allow_comment_reactions: true,
                allow_comment_replies: true,
                notify_on_comment: false,
            };
            await topicData.fetchAndUpdateTopicData({comment_settings: new_comment_settings}, this.amplitude_callsite);
        }
    }

    async enableCommentRepliesChange(e: CustomEvent) {
        const comment_settings = topicData.topic?.comment_settings;
        if (comment_settings && topicData.topic?._id) {
            comment_settings.allow_comment_replies = !comment_settings.allow_comment_replies;
            const new_comment_settings = {...comment_settings};
            await topicData.fetchAndUpdateTopicData({comment_settings: new_comment_settings}, this.amplitude_callsite);
        } else {
            // create new comment settings
            const new_comment_settings: CommentSettings  = {
                allow_comments: true,
                allow_comment_reactions: true,
                allow_comment_replies: true,
                notify_on_comment: false,
            };
            await topicData.fetchAndUpdateTopicData({comment_settings: new_comment_settings}, this.amplitude_callsite);
        }
    }

    async allowCommentReactionsChange(e: CustomEvent) {
        const comment_settings = topicData.topic?.comment_settings;
        if (comment_settings && topicData.topic?._id) {
            comment_settings.allow_comment_reactions = !comment_settings.allow_comment_reactions;
            const new_comment_settings = {...comment_settings};
            await topicData.fetchAndUpdateTopicData({comment_settings: new_comment_settings}, this.amplitude_callsite);
        } else {
            // create new comment settings
            const new_comment_settings: CommentSettings  = {
                allow_comments: true,
                allow_comment_reactions: true,
                allow_comment_replies: true,
                notify_on_comment: false,
            };
            await topicData.fetchAndUpdateTopicData({comment_settings: new_comment_settings}, this.amplitude_callsite);
        }
    }

    async changeCommentTitleExists(e: CustomEvent) {
        const comment_settings = topicData.topic?.comment_settings;
        if (comment_settings && topicData.topic?._id) {
            // if comment title exists, make it undefined
            if (comment_settings.comment_title) {
                comment_settings.comment_title = undefined;
            } else {
                comment_settings.comment_title = {singular: 'Comment', plural: 'Comments'};
            }
            const new_comment_settings = {...comment_settings};
            await topicData.fetchAndUpdateTopicData({comment_settings: new_comment_settings}, this.amplitude_callsite);
        } else {
            // create new comment settings
            const new_comment_settings: CommentSettings  = {
                allow_comments: true,
                allow_comment_reactions: true,
                allow_comment_replies: true,
                comment_title: undefined,
                notify_on_comment: false,
            };
            await topicData.fetchAndUpdateTopicData({comment_settings: new_comment_settings}, this.amplitude_callsite);
        }
    }

    async changeCommentTitleSingular(e: CustomEvent) {
        const comment_settings = topicData.topic?.comment_settings;
        if (comment_settings && topicData.topic?._id && comment_settings.comment_title) {
            comment_settings.comment_title.singular = (e.target as HTMLInputElement).value;

            const new_comment_settings = {...comment_settings};
            await topicData.fetchAndUpdateTopicData({comment_settings: new_comment_settings}, this.amplitude_callsite);
        } else {
            // create new comment settings
            const new_comment_settings: CommentSettings  = {
                allow_comments: true,
                allow_comment_reactions: true,
                allow_comment_replies: true,
                comment_title: e.detail.value,
                notify_on_comment: false,
            };
            await topicData.fetchAndUpdateTopicData({comment_settings: new_comment_settings}, this.amplitude_callsite);
        }
    }

    async changeCommentTitlePlural(e: CustomEvent) {
        const comment_settings = topicData.topic?.comment_settings;
        if (comment_settings && topicData.topic?._id && comment_settings.comment_title) {
            comment_settings.comment_title.plural = (e.target as HTMLInputElement).value;

            const new_comment_settings = {...comment_settings};
            await topicData.fetchAndUpdateTopicData({comment_settings: new_comment_settings}, this.amplitude_callsite);
        } else {
            // create new comment settings
            const new_comment_settings: CommentSettings  = {
                allow_comments: true,
                allow_comment_reactions: true,
                allow_comment_replies: true,
                comment_title: e.detail.value,
                notify_on_comment: false,
            };
            await topicData.fetchAndUpdateTopicData({comment_settings: new_comment_settings}, this.amplitude_callsite);
        }
    }

    async changeCommentInstructions(e: CustomEvent) {
        const comment_settings = topicData.topic?.comment_settings;
        if (comment_settings && topicData.topic?._id) {
            comment_settings.comment_instructions = (e.target as HTMLInputElement).value;
            const new_comment_settings = {...comment_settings};
            await topicData.fetchAndUpdateTopicData({comment_settings: new_comment_settings}, this.amplitude_callsite);
        } else {
            // create new comment settings
            const new_comment_settings: CommentSettings  = {
                allow_comments: true,
                allow_comment_reactions: true,
                allow_comment_replies: true,
                comment_instructions: e.detail.value,
                notify_on_comment: false,
            };
            await topicData.fetchAndUpdateTopicData({comment_settings: new_comment_settings}, this.amplitude_callsite);
        }
    }

    async enableCommentNotificationsChange(e: CustomEvent) {
        const comment_settings = topicData.topic?.comment_settings;
        if (comment_settings && topicData.topic?._id) {
            comment_settings.notify_on_comment = !comment_settings.notify_on_comment;
            const new_comment_settings = {...comment_settings};
            await topicData.fetchAndUpdateTopicData({comment_settings: new_comment_settings}, this.amplitude_callsite);
        } else {
            // create new comment settings
            const new_comment_settings: CommentSettings  = {
                allow_comments: true,
                allow_comment_reactions: true,
                allow_comment_replies: true,
                notify_on_comment: true,
            };
            await topicData.fetchAndUpdateTopicData({comment_settings: new_comment_settings}, this.amplitude_callsite);
        }
    }

    renderCommentSettings() {
        return html`
            <settings-card header="Comment Settings">
                <div class="checkbox-wrapper">
                    <form-checkbox ?disabled="${!featuresInfo?.customizeAddPointFormEnabled}"
                        id="allowComments"
                        name="allowComments"
                        class_name="blue"
                        ?checked="${topicData.topic?.comment_settings?.allow_comments ?? false}"
                        @change="${this.enableCommentsChange}"
                        ></form-checkbox>
                    <span>Enable Comments on Point Cards</span>
                </div>
                ${topicData.topic?.comment_settings?.allow_comments ?? false ? html`
                    <div class="checkbox-wrapper">
                        <form-checkbox ?disabled="${!featuresInfo?.customizeAddPointFormEnabled}"
                            id="allowCommentReplies"
                            name="allowCommentReplies"
                            class_name="blue"
                            ?checked="${topicData.topic?.comment_settings?.allow_comment_replies ?? true}"
                            @change="${this.enableCommentRepliesChange}"
                            >
                        </form-checkbox>
                        <span>Allow Users to Reply to Comments</span>
                    </div>
                    <h4>Comment Title</h4>
                    <p class="subtext">You can call "Comments" something else, like "Stories" or "Feedback".</p>
                    <div class="checkbox-wrapper">
                        <form-checkbox ?disabled="${!featuresInfo?.customizeAddPointFormEnabled}"
                            id="changeCommentTitle"
                            name="changeCommentTitle"
                            class_name="blue"
                            ?checked="${isDefined(topicData.topic?.comment_settings?.comment_title)}"
                            @change="${this.changeCommentTitleExists}"
                            >
                        </form-checkbox>
                        <span>Customize Comment Title</span>
                    </div>

                    ${topicData.topic?.comment_settings?.comment_title ? html`
                        <proxi-textfield ?disabled="${!featuresInfo?.customizeAddPointFormEnabled}"
                            outlined
                            class="input__challenge-title"
                            id="comment-title"
                            type="text"
                            label="Comment Title (Singular)"
                            placeholder="Comments"
                            value="${topicData.topic?.comment_settings?.comment_title.singular ?? 'Comment'}"
                            @change="${this.changeCommentTitleSingular}"
                        > </proxi-textfield>
                        <proxi-textfield ?disabled="${!featuresInfo?.customizeAddPointFormEnabled}"
                            outlined
                            class="input__challenge-title"
                            id="comment-title"
                            type="text"
                            label="Comment Title (Plural)"
                            placeholder="Comments"
                            value="${topicData.topic?.comment_settings?.comment_title.plural ?? 'Comments'}"
                            @change="${this.changeCommentTitlePlural}"
                        > </proxi-textfield>
                    ` : ''}

                    <h4>Comment Instructions</h4>
                    <p class="subtext">Provide instructions for participants to follow when submitting comments. Leave blank for no instructions.</p>
                    <proxi-textarea ?disabled="${!featuresInfo?.customizeAddPointFormEnabled}"
                        outlined
                        class="input__challenge-title"
                        id="comment-instructions"
                        type="text"
                        label="Comment Instructions"
                        placeholder="Instructions that will appear on the comment form."
                        value="${topicData.topic?.comment_settings?.comment_instructions ?? ''}"
                        @change="${this.changeCommentInstructions}"
                    >
                    </proxi-textarea>

                    <div class="checkbox-wrapper">
                        <form-checkbox ?disabled="${!featuresInfo?.customizeAddPointFormEnabled}"
                            id="notifyOnComment"
                            name="notifyOnComment"
                            class_name="blue"
                            ?checked="${topicData.topic?.comment_settings?.notify_on_comment ?? false}"
                            @change="${this.enableCommentNotificationsChange}"
                            >
                        </form-checkbox>
                        <span>Notify me via email when a new comment is posted</span>
                    </div>
                ` : ''}
            </settings-card>
        `;
    }

    static get styles() {
        return css`

            .wrapper {
                display: flex;
                flex-direction: column;
                overflow: hidden auto;
                box-sizing: border-box;
                width: 100%;
                height: 100%
            }

            .header-title-and-buttons {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
                padding: 10px;
                font-size: 18px;
                background-color: var(--theme-border-color);
                border-bottom: 1px solid var(--theme-border-color);
            }

            .left-header-content {
                display: flex;
                flex-direction: row;
                align-items:center;
                font-size: 0.8rem;
                gap: 5px;
                flex: 1;
                flex-wrap: wrap;
            }

            .checkboxHolder {
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .right-header-content {
                display: flex;
                flex-direction: row;
                align-items:center;
                font-size: 0.8rem;
                gap:5px;
                justify-content: flex-end;
                flex: 1;
                flex-wrap: wrap;
            }
            .modalContent {
                position: relative;
                width: 100%;
                height: auto;
                overflow-y: auto;
                box-sizing: border-box;
                height: 100%;
                display: flex;
                flex-direction: column;
            }

            .middle-header-content {
                flex: 1;
                font-size: 1rem;
                text-align:center;
            }

            .header-title-and-buttons h4 {
                margin: 5px 0;
            }

            .tabs {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0 20px;
                background-color: var(--theme-white-color);
                border-bottom: 1px solid var(--theme-border-color);
                box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
            }

            .tab__content {
                padding: 20px;
                display: flex;
                gap: 20px;
                flex: 1;
                overflow: hidden auto;
                justify-content: flex-start;
                align-items: stretch;
                background-color: var(--theme-border-color);
                flex-direction: column;
            }

            .sub-title {
                margin-bottom: 0px;
                color: var(--theme-input-color);
                display: inline-flex;
                --mdc-icon-size: 16px;
            }

            p {
                margin-top: 5px;
                margin-bottom: 5px;
            }

            .general-stats-area {
                display: flex;
                flex-direction: column;
                gap: 10px;
                flex: 1;
            }

            h4.sub-title {
                font-weight: 600;
                font-size: 12px;
                line-height: 100%;
                color: #000000;
                text-align: left;
                margin: 15px 0 5px;
                display: flex;
                align-items: center;
                flex-direction: row;
            }

            /* width */
            .tab__content::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .tab__content::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .tab__content::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            #holder {
                display: flex;
                flex-direction: column;
                height: 100%;
            }

            #container {
                flex-direction: column;
                overflow: hidden auto;
                padding-right: 20px;
                box-sizing: border-box;
                flex: 1;
            }

            /* width */
            #container::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            #container::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            #container::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .title {
                color: var(--main-primary-color);
                text-align: center;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                margin: 5px 0;
                text-align: left;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .sub-title {
                color: var(--theme-input-color);
                display: inline-flex;
                --mdc-icon-size: 16px;
                margin-top: 5px;
            }

            .save {
                --mdc-theme-primary: var(--main-success-color);
            }

            .green {
                --mdc-theme-primary: var(--main-success-color);
            }

            .gray {
                --mdc-theme-primary: var(--main-disabled-color);
            }

            .save-actions {
                display: flex;
                justify-content: flex-end;
                margin: 0.5rem;
            }

            .visibility-square {
                display: flex;
                margin-left: 0.5rem;
                color: var(--theme-input-color);
                border: 1px solid var(--theme-input-color);
                border-radius: 5px;
                padding: 0.1rem 0.25rem;
                align-items: center;
                font-size: 0.8rem;
            }

            .manage-participants-and-results {
                display: flex;
                flex-direction: row;
                gap: 10px;
                height: 100%;
            }

            .individual-participant-drawer {
                border-right: 1px solid var(--theme-border-color);
                box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
                background-color: var(--theme-white-color);
                padding: 1rem;
                transition: width 0.3s;
                display: flex;
                flex-direction: column;
                display: flex;
                overflow-y: auto;
            }

            .individual-participant-drawer.open {
                width: 30%;
            }

            .individual-participant-drawer.closed {
                width: 0;
                padding: 0;
            }

            .helpSubHeader {
                display: inline-flex;
                margin-bottom: 10px;
                font-size: 0.75rem;
                color: var(--main-primary-color);
                align-items: center;
            }

            .summary {
                display: flex;
                align-items: center;
                justify-content: space-between;
            }

            .summary-section {
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .disabled {
                background-color: var(--theme-border-color);
                color: var(--theme-input-color);
                pointer-events: none;
            }

            .move {
                margin-left: 1rem;
            }

            .summary mwc-icon {
                margin-right: 0.5rem;
                --mdc-icon-size: 20px;
            }

            .move-icon {
                cursor: pointer;
                margin: 0;
            }

            .move-icon:hover {
                color: var(--main-primary-color);
            }

            .existingRules {
                display: flex;
                flex-direction: column;
                --mdc-icon-size: 16px;
            }

            .existingRules mwc-icon {
                cursor: pointer;
                margin-right: 0.5rem;
                margin-left: 0.5rem;
            }

            .ruleName {
                margin: 0;
                font-size: 0.8rem;
                text-align: center;
                flex: 1;
                border: 1px solid var(--main-secondary-color);
                display: flex;
                align-items: center;
                gap: 5px;
            }

            .rule {
                display: flex;
                flex-direction: column;
                align-items: center;
                border: 1px solid var(--main-primary-color);
                padding: 0.5rem;
            }

            .operator-holder {
                display: flex;
                justify-content: center;
                flex-direction:row;

            }

            .operator {
                text-align: center;
                font-size: 0.6rem;
                font-weight: bold;
                margin-top: 0.25rem;
                margin-bottom: 0.25rem;
                color: white;
                border-radius: 5px;
                padding: 0 0.5rem;
                --mdc-icon-size: 12px;
            }

            .operator mwc-icon {
                margin-left: 0;
                margin-right: 0.25rem;

            }

            .and {
                background-color: var(--main-primary-color);
                color : white;
            }

            .or {
                background-color: var(--main-secondary-color);
                color: white;
            }

            .clickable-or {
                cursor: pointer;
                color: var(--main-secondary-color);
                background-color: white;
                display: inline-flex;
                align-items: center;
            }

            .clickable-or:hover {
                background-color: var(--main-secondary-color);
                color: white;
            }

            .clickable-and {
                cursor: pointer;
                color: var(--main-primary-color);
                background-color: white;
                display: inline-flex;
                align-items: center;
            }

            .clickable-and:hover {
                background-color: var(--main-primary-color);
                color: white;
            }

            .visibilityRules {
                display: flex;
                flex-direction: column;
                margin-top: 1rem;
            }

            .helpSubeHeaderUnderline {
                margin: 0px;
                font-weight: normal;
                font-size: 0.8rem;
                flex: 1;
                text-decoration: underline;
                color: var(--theme-primary-color);
                display: inline-flex;
            }

            form-tooltip {
                font-size: 0.8rem;
            }

            .archiveField {
                cursor: pointer;
                display: flex;
                align-items: flex-end;
                justify-content: flex-end;
                text-decoration: underline;
                color: var(--main-invalid-color);
            }

            .helpHeader {
                color: var(--main-primary-color);
            }

            .input {
                width: 100%;
                padding: auto 5px;
                box-sizing: border-box;
            }

            .checkbox-wrapper {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 8px;
                line-height: 120%;
                color: #102C3D;
                margin-top: 15px;
            }

            .ruleSelectorWrapper {
                display: flex;
                margin-top: 1rem;
                flex-direction: row;
                flex-wrap: wrap;
                align-content: center;
                justify-content: center;
                gap: 3px 5px;
            }

            .gatedSection {
                cursor: pointer;
            }

            .description {
                color: var(--theme-input-color);
            }

            .add-new-rule-wrapper {
                width: 100%;
                display: flex;
                justify-content: center;
                padding-top: 5px;
            }

            .customizedFields__disabled {
                opacity: 0.5;
                touch-action: none;
                pointer-events: none;
            }

            .hidden {
                display: none;
            }

            .ruleSubmissionButtons {
                display: flex;
                justify-content: center;
                margin-top: 5px;
                padding-bottom: 10px;
                gap: 5px;
            }

            @media only screen and (max-width: 550px) {
                .helpHeader {
                    margin: 0.2em 0;
                }
            }
        `;
    }
}
